/**
 * The expense form's line-item schema (pre-`resolveTaxTreatment` shape),
 * used by CreateExpenseForm's InvoiceLineItems editor. Lives outside the
 * component file so the value export doesn't break React fast refresh there.
 */
import { z } from 'zod';
import { refineScanMiss } from '@/lib/taxRateScan';

/**
 * A purchase line's tax rate (expense form and AP bill form): a number
 * (0-100), 'exempt' (legacy purchase lines only), or 'non_reclaimable' —
 * "VAT not reclaimable", a VAT-org purchase-only treatment (docs/TAX.md).
 */
export const taxRateSchema = z.union([
  z.number().min(-1).max(100),
  z.literal('exempt'),
  z.literal('non_reclaimable'),
]);

export const lineItemSchema = z.object({
  expense_account_id: z.string().min(1, 'Expense account is required'),
  description: z.string().min(1, 'Description is required'),
  quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
  unit_price: z.number(),
  tax_rate: taxRateSchema,
  tax_rate_id: z.string().optional(),
  tax_inclusive: z.boolean().default(false),
  is_reverse_charge: z.boolean().default(false),
  exemption_reason: z.string().optional(),
  exemption_reason_other: z.string().optional(),
  /** Placeholder-rate marker for the default-rate fill (form-only, never sent). */
  tax_rate_unset: z.boolean().optional(),
  /** Scanned percentage the fill pass matches (form-only, never sent). */
  tax_rate_hint: z.number().optional(),
  /** The hint is tax ÷ subtotal — matched within ±0.01 (form-only, never sent). */
  tax_rate_hint_computed: z.boolean().optional(),
  /** The scanned VAT amount for the line (form-only, never sent) — the "VAT not reclaimable" gross-up. */
  tax_rate_hint_vat: z.number().optional(),
  tax_rate_hint_vat_base: z.number().optional(),
  /** The net unit price while grossed up for "VAT not reclaimable" (form-only, never sent). */
  tax_rate_hint_net: z.number().optional(),
  /** The unit price the gross-up wrote (form-only, never sent). */
  tax_rate_hint_gross: z.number().optional(),
  /** The user stated the amount after a gross-up — never gross up again (form-only, never sent). */
  tax_rate_hint_amount_kept: z.boolean().optional(),
  /** The user picked in the tax picker ("No VAT" included) — clears the scan-miss block (form-only, never sent). */
  tax_rate_confirmed: z.boolean().optional(),
  /** NL WKR toggle — reveals the category sub-row (form-local, never wired). */
  wkr_enabled: z.boolean().optional(),
  /** NL WKR classification ('' = none); jurisdiction-gated in the UI. */
  wkr_category: z.string().optional(),
}).superRefine(refineScanMiss);

export type ExpenseLineItemFormData = z.infer<typeof lineItemSchema>;
